/**
 * Reading a resume's text without AI (recruitment: bulk resume upload). Pure and free, so thousands
 * of resumes cost nothing and the rules are tests. It pulls out what can be found reliably with plain
 * code (email, phone, links, a likely name, skills from the dictionary, years of experience from the
 * date ranges) and says how sure it is. Only a resume it is NOT sure about is worth sending to AI.
 *
 * Where it can be wrong it says so (`notes`), rather than looking confident. A resume it cannot find
 * an email in is reported as unreadable, because nobody can be contacted without one.
 */

import { findSkills } from './skill-dictionary.js';
import { normalizePhone } from './candidate-import.js';

export type ResumeReadStatus = 'read' | 'check' | 'failed';

export interface ResumeReading {
  email: string | null;
  phone: string | null;
  linkedinUrl: string | null;
  firstName: string | null;
  lastName: string | null;
  skills: string[];
  /** Years of work, from the dates; null when no work dates were found. */
  experienceYears: number | null;
  status: ResumeReadStatus;
  /** Plain-language reasons a person should look at this one. */
  notes: string[];
  /** The reading is weak enough that an AI pass is worth its cost. */
  worthAskingAi: boolean;
}

const EMAIL = /[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}/;
const PHONE = /(?:\+?\d[\d\s().-]{8,16}\d)/g;
const LINKEDIN = /(?:https?:\/\/)?(?:[a-z]{2,3}\.)?linkedin\.com\/in\/[A-Za-z0-9_%-]+/i;

const HEADING_WORDS =
  /\b(resume|curriculum|vitae|cv|profile|summary|objective|contact|address|email|phone|mobile|skills|experience|education|declaration|references?|personal|details|page)\b/i;

const NAME_WORD = /^[A-Z][A-Za-z'’.-]*$/;
const SHOUTING_WORD = /^[A-Z][A-Z'’.-]*$/;

const titleCase = (word: string) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();

/** The first of the opening lines that looks like a person's name. */
export function guessName(text: string): { firstName: string; lastName: string } | null {
  const lines = text
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter(Boolean)
    .slice(0, 8);
  for (const line of lines) {
    if (/[@\d:/\\|]/.test(line) || HEADING_WORDS.test(line)) continue;
    const words = line.split(/\s+/);
    if (words.length < 2 || words.length > 4) continue;
    const plain = words.every((w) => NAME_WORD.test(w));
    const shouting = words.every((w) => SHOUTING_WORD.test(w) && w.length > 1);
    if (!plain && !shouting) continue;
    const cased = shouting ? words.map(titleCase) : words;
    return { firstName: cased[0]!, lastName: cased.slice(1).join(' ') };
  }
  return null;
}

// ---------------------------------------------------------------------------
// Years of experience from date ranges
// ---------------------------------------------------------------------------

const MONTHS = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'];
const MONTH = '(jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec)[a-z]*\\.?';
const NOW = '(present|current|currently|now|till date|to date|ongoing)';
const SEP = '\\s*(?:-|–|—|to|till|until)\\s*';
// "Jan 2019 - Mar 2023", "Jan 2019 - Present"
const MONTH_RANGE = new RegExp(`${MONTH}\\s*,?\\s*(\\d{4})${SEP}(?:${MONTH}\\s*,?\\s*(\\d{4})|${NOW})`, 'gi');
// "03/2019 - 05/2023"
const NUMERIC_RANGE = new RegExp(`\\b(\\d{1,2})[/.](\\d{4})${SEP}(?:(\\d{1,2})[/.](\\d{4})|${NOW})`, 'gi');
// "2019 - 2023", "2019 - Present"
const YEAR_RANGE = new RegExp(`\\b((?:19|20)\\d{2})${SEP}(?:((?:19|20)\\d{2})|${NOW})\\b`, 'gi');

const EDUCATION =
  /\b(b\.?\s?tech|b\.?\s?e\b|m\.?\s?tech|bachelor|master|mba|b\.?\s?sc|m\.?\s?sc|b\.?\s?com|m\.?\s?com|bca|mca|b\.?\s?a\b|m\.?\s?a\b|ph\.?\s?d|diploma|degree|university|college|school|institute|hsc|ssc|cbse|icse|12th|10th|graduat|education|cgpa|gpa|percentage)\b/i;

interface Interval {
  start: number;
  end: number;
}

const monthIndex = (year: number, month: number) => year * 12 + month;

/** Total months covered by the union of intervals (overlapping jobs count once). */
function unionMonths(intervals: Interval[]): number {
  const sorted = [...intervals].sort((a, b) => a.start - b.start);
  let total = 0;
  let current: Interval | null = null;
  for (const interval of sorted) {
    if (!current || interval.start > current.end) {
      if (current) total += current.end - current.start;
      current = { ...interval };
    } else current.end = Math.max(current.end, interval.end);
  }
  if (current) total += current.end - current.start;
  return total;
}

/**
 * Years of work, from the date ranges in the text. Ranges on or beside lines that talk about
 * education are ignored (a degree is not a job). Returns null when no work date range is found.
 */
export function estimateExperienceYears(text: string, now: Date = new Date()): number | null {
  const nowIndex = monthIndex(now.getUTCFullYear(), now.getUTCMonth());
  const lines = text.split(/\r?\n/);
  const intervals: Interval[] = [];

  lines.forEach((line, i) => {
    const near = [lines[i - 1] ?? '', line, lines[i + 1] ?? ''].join(' ');
    if (EDUCATION.test(near)) return;

    const add = (start: number, end: number | null) => {
      const to = Math.min(end ?? nowIndex, nowIndex);
      if (start >= to || to - start > 12 * 50 || start > nowIndex) return;
      intervals.push({ start, end: to });
    };
    const monthNo = (name: string) => MONTHS.indexOf(name.slice(0, 3).toLowerCase());

    let matchedHere = false;
    for (const m of line.matchAll(MONTH_RANGE)) {
      matchedHere = true;
      const start = monthIndex(Number(m[2]), monthNo(m[1]!));
      // The last month named counts as worked: Jan 2020 - Dec 2022 is three years.
      add(start, m[4] ? monthIndex(Number(m[4]), monthNo(m[3]!)) + 1 : null);
    }
    for (const m of line.matchAll(NUMERIC_RANGE)) {
      matchedHere = true;
      const startMonth = Number(m[1]) - 1;
      if (startMonth < 0 || startMonth > 11) continue;
      const start = monthIndex(Number(m[2]), startMonth);
      add(start, m[4] ? monthIndex(Number(m[4]), Number(m[3]) - 1) + 1 : null);
    }
    if (!matchedHere) {
      for (const m of line.matchAll(YEAR_RANGE)) {
        // Year-only: the plain difference in years (2019 - 2023 is four years).
        const start = monthIndex(Number(m[1]), 0);
        add(start, m[2] ? monthIndex(Number(m[2]), 0) : null);
      }
    }
  });

  if (intervals.length === 0) return null;
  const years = unionMonths(intervals) / 12;
  return Math.round(years * 10) / 10;
}

/** "5+ years of experience" stated outright, when there are no dates to count. */
export function statedExperienceYears(text: string): number | null {
  const match =
    text.match(/(\d{1,2}(?:\.\d)?)\s*\+?\s*(?:years?|yrs?)\b[^.\n]{0,40}\bexperience/i) ??
    text.match(/experience\s*(?:of|:)?\s*(\d{1,2}(?:\.\d)?)\s*\+?\s*(?:years?|yrs?)/i);
  if (!match) return null;
  const years = Number(match[1]);
  return Number.isFinite(years) && years >= 0 && years <= 50 ? years : null;
}

// ---------------------------------------------------------------------------
// The reading
// ---------------------------------------------------------------------------

const MIN_TEXT_CHARS = 120;

export function readResume(
  text: string,
  options: { defaultCountryCode: string | null; now?: Date },
): ResumeReading {
  const notes: string[] = [];
  const clean = text.split('\u0000').join(' ');

  if (clean.replace(/\s/g, '').length < MIN_TEXT_CHARS) {
    return {
      email: null, phone: null, linkedinUrl: null, firstName: null, lastName: null,
      skills: [], experienceYears: null, status: 'failed',
      notes: ['Very little text could be read. It may be a photo or a blank file'],
      worthAskingAi: false,
    };
  }

  const email = EMAIL.exec(clean)?.[0]?.toLowerCase() ?? null;

  let phone: string | null = null;
  for (const candidate of clean.match(PHONE) ?? []) {
    phone = normalizePhone(candidate, options.defaultCountryCode);
    if (phone) break;
  }

  const linkedin = LINKEDIN.exec(clean)?.[0] ?? null;
  const linkedinUrl = linkedin ? (/^https?:\/\//i.test(linkedin) ? linkedin : `https://${linkedin}`) : null;

  const name = guessName(clean);
  const skills = findSkills(clean).slice(0, 20);

  const computed = estimateExperienceYears(clean, options.now);
  const experienceYears = computed ?? statedExperienceYears(clean);

  if (!email) notes.push('No email address found');
  if (!name) notes.push('Could not tell the name');
  if (skills.length < 3) notes.push('Few recognisable skills');
  if (experienceYears === null) notes.push('Could not work out years of experience');

  let status: ResumeReadStatus;
  if (!email) status = 'failed';
  else if (name && skills.length >= 3 && experienceYears !== null) status = 'read';
  else status = 'check';

  return {
    email,
    phone,
    linkedinUrl,
    firstName: name?.firstName ?? null,
    lastName: name?.lastName ?? null,
    skills,
    experienceYears,
    status,
    notes,
    // Worth paying for only when a person would otherwise have to fix it by hand.
    worthAskingAi: status !== 'read',
  };
}
